import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Spinner,
  TableCellLayout,
  TableColumnDefinition,
  Text,
} from '@fluentui/react-components';
import { Add24Regular, Delete24Regular, Edit24Regular, DocumentBulletList24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { ContractNegotiationVersion } from '../../../models/Entities/Contracts/ContractNegotiationVersion';
import { NewContractNegotiationVersion } from '../../../models/Entities/Contracts/NewContractNegotiationVersion';
import * as ComponentFunctions from './ContractNegotiationVersionList.Functions';
import { useContractNegotiationVersionListStyles } from './ContractNegotiationVersionList.Styles';
import ContractNegotiationVersionFormComponent from '../ContractNegotiationVersionForm/ContractNegotiationVersionForm.Component';
import ContractNegotiationVersionConceptListComponent from '../ContractNegotiationVersionConceptList/ContractNegotiationVersionConceptList.Component';
import { mapStateToProps, mapDispatchToProps } from './ContractNegotiationVersionList.Functions';

const formatDate = (date: Date | undefined | string | null): string => {
  if (!date) return '-';
  return new Date(date as string).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const shortId = (id: string): string => {
  if (!id) return '-';
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
};

const ContractNegotiationVersionListComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const styles = useContractNegotiationVersionListStyles();

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [selectedVersion, setSelectedVersion] = useState<ContractNegotiationVersion | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<ContractNegotiationVersion | undefined>(undefined);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  useEffect(() => {
    props.GetContractNegotiationVersions(props.contractNegotiationId, props.contractId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.contractNegotiationId, props.contractId]);

  const handleAddNew = () => {
    setSelectedVersion(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (version: ContractNegotiationVersion) => {
    setSelectedVersion(version);
    setIsFormOpen(true);
  };

  const handleFormDismiss = () => {
    setIsFormOpen(false);
    setSelectedVersion(undefined);
  };

  const handleSave = (entity: NewContractNegotiationVersion | ContractNegotiationVersion) => {
    const isExisting = 'id' in entity && !!(entity as ContractNegotiationVersion).id;
    if (isExisting) {
      props.UpdateContractNegotiationVersion(entity as ContractNegotiationVersion, props.contractId);
    } else {
      props.CreateContractNegotiationVersion(entity as NewContractNegotiationVersion, props.contractId);
    }
    setIsFormOpen(false);
    setSelectedVersion(undefined);
  };

  const handleDeleteRequest = (version: ContractNegotiationVersion) => {
    setDeleteTarget(version);
  };

  const handleDeleteConfirm = () => {
    if (deleteTarget) {
      props.DeleteContractNegotiationVersion(
        deleteTarget.id,
        props.contractId,
        deleteTarget.contractNegotiationId
      );
    }
    setDeleteTarget(undefined);
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(undefined);
  };

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const columns: TableColumnDefinition<ContractNegotiationVersion>[] = useMemo(
    () => [
      createTableColumn<ContractNegotiationVersion>({
        columnId: 'id',
        renderHeaderCell: () => t('contractNegotiationVersionList.columns.id', 'ID'),
        renderCell: (item) => <TableCellLayout title={item.id}>{shortId(item.id)}</TableCellLayout>,
      }),
      createTableColumn<ContractNegotiationVersion>({
        columnId: 'negotiationDate',
        renderHeaderCell: () => t('contractNegotiationVersionList.columns.date', 'Fecha de negociación'),
        renderCell: (item) => <TableCellLayout>{formatDate(item.negotiationDate)}</TableCellLayout>,
      }),
      createTableColumn<ContractNegotiationVersion>({
        columnId: 'actions',
        renderHeaderCell: () => t('contractNegotiationVersionList.columns.actions', 'Acciones'),
        renderCell: (item) => (
          <TableCellLayout>
            <div className={styles.actionCell}>
              <Button
                appearance="subtle"
                size="small"
                icon={<Edit24Regular />}
                onClick={() => handleEdit(item)}
                title={t('contractNegotiationVersionList.actions.edit', 'Editar')}
              />
              <Button
                appearance="subtle"
                size="small"
                icon={<Delete24Regular />}
                onClick={() => handleDeleteRequest(item)}
                title={t('contractNegotiationVersionList.actions.delete', 'Eliminar')}
              />
              <Button
                appearance="subtle"
                size="small"
                icon={<DocumentBulletList24Regular />}
                onClick={() => toggleExpand(item.id)}
                title={t('contractNegotiationVersionList.actions.concepts', 'Ver conceptos')}
              />
            </div>
          </TableCellLayout>
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, styles]
  );

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <Text weight="semibold" size={300}>
          {t('contractNegotiationVersionList.title', 'Versiones de negociación')}
        </Text>
        <Button appearance="primary" size="small" icon={<Add24Regular />} onClick={handleAddNew}>
          {t('contractNegotiationVersionList.addButton', 'Agregar versión')}
        </Button>
      </div>

      {props.isLoading && (
        <Spinner size="small" label={t('contractNegotiationVersionList.loading', 'Cargando versiones...')} />
      )}

      {props.errorMessage && (
        <MessageBar intent="error">
          <MessageBarBody>{props.errorMessage}</MessageBarBody>
        </MessageBar>
      )}

      {!props.isLoading && !props.errorMessage && props.versions.length === 0 && (
        <div className={styles.emptyState}>
          <Text>{t('contractNegotiationVersionList.empty', 'No hay versiones registradas.')}</Text>
        </div>
      )}

      {props.versions.length > 0 && (
        <div className={styles.gridWrapper}>
          <DataGrid
            items={props.versions}
            columns={columns}
            sortable
            resizableColumns
            focusMode="composite"
            getRowId={(item) => item.id}
          >
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell }) => (
                  <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody<ContractNegotiationVersion>>
              {({ item, rowId }) => (
                <React.Fragment key={rowId}>
                  <DataGridRow<ContractNegotiationVersion>>
                    {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                  </DataGridRow>
                  {expandedItems.includes(item.id) && (
                    <div className={styles.fullRow}>
                      <div className={styles.versionPanel}>
                        <ContractNegotiationVersionConceptListComponent
                          contractNegotiationVersionId={item.id}
                          contractNegotiationId={props.contractNegotiationId}
                          contractId={props.contractId}
                        />
                      </div>
                    </div>
                  )}
                </React.Fragment>
              )}
            </DataGridBody>
          </DataGrid>
        </div>
      )}

      <ContractNegotiationVersionFormComponent
        contractNegotiationId={props.contractNegotiationId}
        contractId={props.contractId}
        version={selectedVersion}
        isOpen={isFormOpen}
        onDismiss={handleFormDismiss}
        onSave={handleSave}
      />

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(_, data) => { if (!data.open) handleDeleteCancel(); }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('contractNegotiationVersionList.deleteDialog.title', 'Eliminar versión')}</DialogTitle>
            <DialogContent>
              {t(
                'contractNegotiationVersionList.deleteDialog.message',
                '¿Está seguro que desea eliminar esta versión de negociación?'
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={handleDeleteCancel}>
                {t('contractNegotiationVersionList.deleteDialog.cancel', 'Cancelar')}
              </Button>
              <Button appearance="primary" onClick={handleDeleteConfirm}>
                {t('contractNegotiationVersionList.deleteDialog.confirm', 'Eliminar')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export { ContractNegotiationVersionListComponent };
export default connect(mapStateToProps, mapDispatchToProps)(ContractNegotiationVersionListComponent as React.ComponentType<ComponentFunctions.OwnProps>);
